import React, { type ComponentType, type ReactNode, useEffect, useMemo, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink, useHistory } from "react-router-dom";
import { toast } from "sonner";

import { getDoctorsTasks, logoutUser } from "~/actions/dashboard";
import { useGetChatThreadsQuery } from "~/api";
import { landingRoute } from "~/common/landing-route";
import {
  GraduationCap as AcademicCapIcon,
  ArrowUp as ArrowUpIcon,
  MessagesSquare as ChatBubbleLeftRightIcon,
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
  Files as DocumentDuplicateIcon,
  FileText as DocumentTextIcon,
  ExpertConsultation as ExpertConsultationIcon,
  Flag as FlagIcon,
  LayoutDashboard as LayoutDashboardIcon,
  LogOut as LogOutIcon,
  Shapes as ShapesIcon,
  SquareMenu as SquareMenuIcon,
  Truck as TruckIcon,
  Users as UsersIcon,
  WalletCards as WalletCardsIcon,
} from "~/common/redesign-v1/icons";
import { isUser, useOptionalUser } from "~/common/user";
import { formatPhoneNumber } from "~/common/utils";
import { formatPersonName } from "~/components/common/PersonName";
import { API_ACCOUNT_AVATAR, API_DOCTOR_OFFERS_PRIVATE_STATIC, CLINICAL_CASE_LIBRARY_URL } from "~/config";
import { filterVisibleChatThreads } from "~/features/chat/chat-thread-visibility";
import { useAwaitingPatientsCount, usePaymentsCount, useShipmentsCount, useUnpaidEventsCount } from "~/hooks/use-user";
import { selectUser } from "~/slices/user";
import { useAppDispatch, useAppSelector } from "~/store";
import type { TUser } from "~/types/user";

import { cn } from "../cn";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { BrandLogo } from "./brand-logo";
import { RedesignToolbarContext } from "./page-toolbar";

type IconType = ComponentType<{ className?: string }>;
type MsgId = FormatjsIntl.Message["ids"];

interface NavItem {
  key: string;
  id: MsgId;
  defaultMessage: string;
  Icon: IconType;
  badge?: number;
  route?: string;
  href?: string;
}

const PROFILE_ROUTE = "/pages/my_account";
const DELIVERY_ROUTE = "/pages/shipments";

const HOME_ROUTE = landingRoute();

const NAV_GROUPS = [
  [
    { key: "achievements", id: "redesign.nav.achievements", defaultMessage: "My achievements", Icon: LayoutDashboardIcon, route: "/pages/achievements" },
    { key: "patients", id: "redesign.nav.patients", defaultMessage: "Patients", Icon: UsersIcon, route: "/pages/patients" },
    { key: "payments", id: "redesign.nav.payments", defaultMessage: "Payments", Icon: WalletCardsIcon, route: "/pages/payments" },
    { key: "delivery", id: "redesign.nav.delivery", defaultMessage: "Delivery", Icon: TruckIcon, route: DELIVERY_ROUTE },
    { key: "chat", id: "redesign.nav.chat", defaultMessage: "Chat", Icon: ChatBubbleLeftRightIcon, route: "/pages/chat" },
  ],
  [
    { key: "learning", id: "redesign.nav.learning", defaultMessage: "Events", Icon: AcademicCapIcon, route: "/pages/events" },
    { key: "consultations", id: "redesign.nav.consultations", defaultMessage: "Expert consultations", Icon: ExpertConsultationIcon, route: "/pages/meetings" },
    { key: "cases", id: "redesign.nav.cases", defaultMessage: "Clinical cases", Icon: FlagIcon, href: CLINICAL_CASE_LIBRARY_URL },
    { key: "instructions", id: "redesign.nav.instructions", defaultMessage: "Instructions", Icon: DocumentTextIcon, route: "/pages/manuals" },
  ],
  [
    { key: "documents", id: "redesign.nav.documents", defaultMessage: "Documents", Icon: DocumentDuplicateIcon, route: "/pages/documents" },
    { key: "materials", id: "redesign.nav.materials", defaultMessage: "Patient materials", Icon: ShapesIcon, route: "/pages/promo" },
  ],
] satisfies NavItem[][];

type ShellNavTone = "sidebar" | "menu";

const BOTTOM_NAV = [
  { key: "patients", id: "redesign.nav.patients", defaultMessage: "Patients", Icon: UsersIcon, route: "/pages/patients" },
  { key: "delivery", id: "redesign.nav.delivery", defaultMessage: "Delivery", Icon: TruckIcon, route: DELIVERY_ROUTE },
  { key: "payments", id: "redesign.nav.payments", defaultMessage: "Payments", Icon: WalletCardsIcon, route: "/pages/payments" },
  { key: "chat", id: "redesign.nav.chat", defaultMessage: "Chat", Icon: ChatBubbleLeftRightIcon, route: "/pages/chat" },
  { key: "more", id: "redesign.nav.more", defaultMessage: "More", Icon: SquareMenuIcon },
] satisfies NavItem[];

function useShellNavBadges() {
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUser);
  const doctorTasks = useAppSelector((state) => state.doctorTasks);
  const awaitingPatients = useAwaitingPatientsCount();
  const payments = usePaymentsCount();
  const shipments = useShipmentsCount();
  const unpaidEvents = useUnpaidEventsCount();
  const { data: chatThreads } = useGetChatThreadsQuery(undefined, { pollingInterval: 5000 });
  const chatUnread = useMemo(() => filterVisibleChatThreads(chatThreads?.threads ?? []).reduce((sum, thread) => sum + (thread.unread_count ?? 0), 0), [chatThreads?.threads]);

  useEffect(() => {
    if (user.account_id && !doctorTasks.hasBeenLoaded) {
      void dispatch(getDoctorsTasks());
    }
  }, [dispatch, doctorTasks.hasBeenLoaded, user.account_id]);

  return (key: string): number | undefined => {
    const counts: Record<string, number> = {
      patients: awaitingPatients,
      payments: payments,
      delivery: shipments,
      chat: chatUnread,
      learning: unpaidEvents,
    };
    const count = counts[key] ?? 0;
    return count > 0 ? count : undefined;
  };
}

export interface RedesignAppShellUser {
  name: string;
  initials: string;
  avatarUrl?: string;
}

type Manager = { role: string; name: string; avatarUrl?: string; phone?: string };

type BreadcrumbLeaf = { id: MsgId; defaultMessage: string; values?: Record<string, string | number> };
type BreadcrumbRoot = { id: MsgId; defaultMessage: string; to?: string };
export type BreadcrumbTrailItem = {
  id: MsgId;
  defaultMessage: string;
  values?: Record<string, string | number>;
  to?: string;
};

export interface RedesignAppShellProps {
  activeNav?: string;
  breadcrumbRoot?: BreadcrumbRoot;
  breadcrumbTrail?: BreadcrumbTrailItem[];
  breadcrumbLeaf?: BreadcrumbLeaf;
  user?: RedesignAppShellUser;
  manager?: Manager;
  /** Constrains the shell to the viewport so an inner region can own scrolling. */
  fullHeight?: boolean;
  plainSurface?: boolean;
  /** Uses compact desktop spacing and constrains the two-column dashboard to the viewport. */
  tightFooter?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}

function NavRow({ item, active, badge, tone, onSelect }: { item: NavItem; active: boolean; badge?: number; tone: ShellNavTone; onSelect(item: NavItem): void }) {
  const { Icon } = item;
  const isSidebar = tone === "sidebar";
  const content = (
    <>
      <Icon className={cn("tw-shrink-0", isSidebar ? "tw-size-4" : "tw-size-5")} />
      <span className={cn("tw-flex-1 tw-truncate", isSidebar ? "tw-text-sm" : "tw-text-base", active && "tw-font-medium")}>
        <FormattedMessage id={item.id} defaultMessage={item.defaultMessage} />
      </span>
      {badge ? (
        <span
          className={cn(
            "tw-flex tw-size-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-bold",
            active ? "tw-bg-primary tw-text-primary-foreground" : isSidebar ? "tw-bg-white tw-text-foreground" : "tw-bg-foreground tw-text-primary-foreground",
          )}
        >
          {badge}
        </span>
      ) : null}
    </>
  );
  const className = cn(
    "tw-flex tw-h-10 tw-w-full tw-cursor-pointer tw-appearance-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-0 tw-bg-transparent tw-py-2 tw-pl-3 tw-pr-2 tw-text-left tw-transition-colors",
    isSidebar
      ? active
        ? "tw-bg-neutral-100 tw-text-stone-800"
        : "tw-text-sidebar-text hover:tw-bg-white/10"
      : active
        ? "tw-bg-canvas-accent tw-text-base tw-text-foreground"
        : "tw-text-base tw-text-sidebar-foreground hover:tw-bg-accent",
  );
  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => onSelect(item)} className={cn(className, "tw-no-underline hover:tw-no-underline")}>
        {content}
      </a>
    );
  }
  return (
    <button type="button" aria-current={active ? "page" : undefined} onClick={() => onSelect(item)} className={className}>
      {content}
    </button>
  );
}

function NavGroups({ activeNav, navBadge, tone, onSelect }: { activeNav?: string; navBadge(key: string): number | undefined; tone: ShellNavTone; onSelect(item: NavItem): void }) {
  return (
    <>
      {NAV_GROUPS.map((group, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 ? <Separator className={cn("tw-my-1.5", tone === "sidebar" && "tw-bg-sidebar-separator")} /> : null}
          {group.map((item) => (
            <NavRow key={item.key} item={item} active={item.key === activeNav} badge={navBadge(item.key)} tone={tone} onSelect={onSelect} />
          ))}
        </React.Fragment>
      ))}
    </>
  );
}

const MGR_BTN =
  "tw-h-12 tw-min-w-0 tw-grow tw-shrink tw-basis-0 tw-rounded-lg tw-border-0 tw-px-5 tw-py-0 tw-text-sm desktop:tw-h-10 desktop:tw-w-[118px] desktop:tw-grow-0 desktop:tw-shrink-0 desktop:tw-basis-auto";

function ManagerCard({ manager, tone, onWrite }: { manager: Manager; tone: ShellNavTone; onWrite(): void }) {
  const isSidebar = tone === "sidebar";
  const intl = useIntl();
  // This library also works on non-secure LAN development origins where navigator.clipboard is unavailable.
  const onPhoneCopied = (_text: string, copied: boolean) => {
    if (copied) {
      toast(intl.formatMessage({ id: "redesign.shell.phone_copied", defaultMessage: "Phone number copied" }));
    }
  };
  return (
    <div className={cn("tw-flex tw-min-w-0 tw-flex-col tw-gap-2.5 tw-rounded-xl tw-p-4", isSidebar ? "tw-bg-sidebar-card" : "tw-bg-canvas-accent tw-shadow-xs")}>
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-relative tw-z-0 tw-shrink-0 hover:tw-z-40">
          <Avatar className="tw-size-[54px] tw-origin-center tw-border tw-border-solid tw-border-border tw-bg-background tw-transition-transform tw-duration-700 tw-ease-in-out hover:tw-z-40 hover:tw-scale-[2.65]">
            <AvatarImage src={manager.avatarUrl} alt={manager.name} className="tw-object-cover" />
            <AvatarFallback className="tw-bg-background tw-text-sm tw-font-medium tw-text-foreground">{manager.name.charAt(0)}</AvatarFallback>
          </Avatar>
        </div>
        <div className="tw-min-w-0 tw-flex-1">
          <p className={cn("tw-m-0 tw-truncate tw-text-sm tw-leading-5", isSidebar ? "tw-text-sidebar-muted" : "tw-text-xs tw-leading-4 tw-text-muted-foreground")}>{manager.role}</p>
          <p className={cn("tw-m-0 tw-truncate tw-text-sm tw-font-medium tw-leading-5", isSidebar ? "tw-text-white" : "tw-text-stone-800")}>{manager.name}</p>
        </div>
      </div>
      <div className="tw-flex tw-min-w-0 tw-flex-row tw-gap-2">
        {manager.phone ? (
          <>
            <Button asChild variant="outline" size="sm" className={cn(MGR_BTN, "tw-bg-background tw-shadow-none desktop:tw-hidden")}>
              <a href={`tel:${manager.phone}`}>
                <FormattedMessage id="redesign.shell.call" defaultMessage="Call" />
              </a>
            </Button>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="tw-hidden tw-shrink-0 desktop:tw-block desktop:tw-w-[118px]">
                    <CopyToClipboard text={manager.phone} onCopy={onPhoneCopied}>
                      <Button type="button" variant="outline" size="sm" className={cn(MGR_BTN, "tw-flex tw-w-full tw-bg-background tw-shadow-none")}>
                        <FormattedMessage id="redesign.shell.call" defaultMessage="Call" />
                      </Button>
                    </CopyToClipboard>
                  </span>
                </TooltipTrigger>
                <TooltipContent data-testid="rd-manager-phone-tooltip" side="bottom" className="tw-text-sm tw-font-medium tw-tabular-nums">
                  {formatPhoneNumber(manager.phone)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        ) : (
          <Button disabled variant="outline" size="sm" className={cn(MGR_BTN, "tw-bg-background tw-shadow-none")}>
            <FormattedMessage id="redesign.shell.call" defaultMessage="Call" />
          </Button>
        )}
        <Button size="sm" className={MGR_BTN} onClick={onWrite}>
          <FormattedMessage id="redesign.shell.write" defaultMessage="Message" />
        </Button>
      </div>
    </div>
  );
}

function initialsFor(firstName?: string, lastName?: string) {
  return `${lastName?.[0] ?? ""}${firstName?.[0] ?? ""}`.toUpperCase() || "—";
}

function shellUserFromStore(currentUser: Partial<TUser>): RedesignAppShellUser {
  if (!isUser(currentUser)) {
    return { name: "", initials: "—" };
  }

  const avatar = currentUser.avatar;
  const hasAvatar = typeof avatar === "string" && avatar.trim() !== "" && avatar !== "None";

  return {
    name: formatPersonName({ person: currentUser, useMiddleName: true, isDoctor: true }),
    initials: initialsFor(currentUser.first_name, currentUser.last_name),
    avatarUrl: currentUser.account_id && hasAvatar ? API_ACCOUNT_AVATAR(currentUser.account_id) : undefined,
  };
}

function BrandLogoButton({ onClick, className, variant = "default" }: { onClick(): void; className?: string; variant?: "default" | "sidebar" | "wordmark-light" }) {
  const intl = useIntl();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={intl.formatMessage({ id: "redesign.shell.home", defaultMessage: "Home" })}
      className={cn("tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0", className)}
    >
      <BrandLogo variant={variant} />
    </button>
  );
}

// Keep the identifying suffix visible while retaining the full value in data-app-version.
const APP_VERSION_VISIBLE_CHARS = 7;

export function AppVersion() {
  const full = process.env.APP_VERSION ?? "";
  const shown = full.length > APP_VERSION_VISIBLE_CHARS ? full.slice(-APP_VERSION_VISIBLE_CHARS) : full;
  return (
    <span data-app-version={full} data-testid="rd-app-version">
      <FormattedMessage id="page.footer.version" defaultMessage="Version" />: {shown}
    </span>
  );
}

function ShellFooter({ className, tone = "light" }: { className?: string; tone?: "light" | "dark" }) {
  const isDark = tone === "dark";
  const linkClass = cn(
    "tw-underline tw-decoration-solid [text-decoration-skip-ink:none] [text-decoration-thickness:from-font] [text-underline-position:from-font]",
    isDark ? "tw-text-white hover:tw-text-sidebar-muted" : "tw-text-stone-500 hover:tw-text-foreground",
  );

  const user = useOptionalUser();
  const legacyLinks = (
    <>
      <a href="https://3d-smile.ru/policy" target="_blank" rel="noopener noreferrer" className={linkClass}>
        <FormattedMessage id="Privacy" defaultMessage="Privacy" />
      </a>
      <NavLink to="/contacts" className={linkClass}>
        <FormattedMessage id="page.footer.contacts" defaultMessage="Contacts" />
      </NavLink>
      {user?.has_signed_offer ? (
        <a href={API_DOCTOR_OFFERS_PRIVATE_STATIC} className={linkClass}>
          <FormattedMessage id="page.footer.offer" defaultMessage="Offer" />
        </a>
      ) : null}
      {user ? (
        <NavLink to="/payment/details" className={linkClass}>
          <FormattedMessage id="page.footer.payment" defaultMessage="Payment" />
        </NavLink>
      ) : null}
      <AppVersion />
    </>
  );

  if (isDark) {
    return (
      <div className={cn("tw-flex tw-flex-col tw-gap-1 tw-font-sans tw-text-xs tw-font-normal tw-leading-4 tw-tracking-normal tw-text-white", className)}>
        <p className="tw-m-0">
          <FormattedMessage id="redesign.shell.contacts" defaultMessage="Contacts: +7 (495) 211-44-54" />
        </p>
        <p className="tw-m-0">
          <FormattedMessage id="redesign.shell.copyright" defaultMessage="{year} © OOO «3D Smile»" values={{ year: new Date().getFullYear() }} />
        </p>
        <nav className="tw-m-0 tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1">{legacyLinks}</nav>
      </div>
    );
  }

  return (
    <div className={cn("tw-flex tw-flex-wrap tw-items-start tw-gap-1 tw-py-2 tw-text-xs tw-leading-4 tw-text-stone-500", className)}>
      <p className="tw-m-0 tw-shrink-0 tw-px-2">
        <FormattedMessage id="redesign.shell.copyright" defaultMessage="{year} © OOO «3D Smile»" values={{ year: new Date().getFullYear() }} />
      </p>
      <p className="tw-m-0 tw-shrink-0 tw-px-2">
        <FormattedMessage id="redesign.shell.contacts" defaultMessage="Contacts: +7 (495) 211-44-54" />
      </p>
      <nav className="tw-m-0 tw-flex tw-min-w-0 tw-flex-1 tw-basis-full tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-px-2 footer-inline:tw-basis-0">{legacyLinks}</nav>
    </div>
  );
}

export function RedesignAppShell({
  activeNav,
  breadcrumbRoot,
  breadcrumbTrail,
  breadcrumbLeaf,
  user,
  manager,
  fullHeight = false,
  plainSurface = false,
  tightFooter = false,
  footer,
  children,
}: RedesignAppShellProps) {
  const intl = useIntl();
  const history = useHistory();
  const currentUser = useAppSelector(selectUser);
  const shouldLoadManagerThread = !manager?.avatarUrl;
  const { data: chatThreads } = useGetChatThreadsQuery(undefined, { skip: !shouldLoadManagerThread });
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [toolbarSlot, setToolbarSlot] = useState<HTMLDivElement | null>(null);
  const [, setStickyOnMobile] = useState(false);
  const toolbarContextValue = useMemo(() => ({ slot: toolbarSlot, setStickyOnMobile }), [toolbarSlot]);
  const dispatch = useAppDispatch();
  const navBadge = useShellNavBadges();
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    if (fullHeight) return;
    const onScroll = () => setShowScrollTop(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [fullHeight]);

  const goHome = () => history.push(HOME_ROUTE);
  const goCurrent = () => history.push(history.location.pathname + history.location.search);
  const crumbClass = (active: boolean) =>
    cn("tw-cursor-pointer tw-truncate tw-border-0 tw-bg-transparent tw-p-0 hover:tw-text-foreground", active ? "tw-font-medium tw-text-foreground" : "tw-text-muted-foreground");
  const sidebarBrand = <BrandLogoButton onClick={goHome} variant="sidebar" />;
  const mobileBarBrand = <BrandLogoButton onClick={goHome} variant="wordmark-light" />;
  const activeNavItem = NAV_GROUPS.flat().find((item) => item.key === activeNav);
  const resolvedUser = useMemo(() => {
    const fromStore = shellUserFromStore(currentUser);
    if (!user) return fromStore;
    return {
      ...fromStore,
      ...user,
      avatarUrl: user.avatarUrl ?? fromStore.avatarUrl,
    };
  }, [currentUser, user]);
  const managerThread = chatThreads?.threads.find((thread) => thread.channel === "personal_manager");
  const managerAssignee = managerThread?.assignee;
  const fallbackManagerName = managerAssignee
    ? formatPersonName({ person: managerAssignee, useMiddleName: true })
    : formatPersonName({ person: currentUser.manager ?? undefined, useMiddleName: true });
  const fallbackManagerAvatarUrl = managerAssignee?.avatar ? API_ACCOUNT_AVATAR(managerAssignee.account_id) : undefined;
  const resolvedManager =
    manager || managerAssignee || currentUser.manager
      ? {
          role: manager?.role ?? intl.formatMessage({ id: "redesign.shell.manager", defaultMessage: "Your manager" }),
          name: manager?.name ?? fallbackManagerName,
          avatarUrl: manager?.avatarUrl ?? fallbackManagerAvatarUrl,
          phone: manager?.phone ?? currentUser.manager?.phone,
        }
      : undefined;

  const onSelect = (item: NavItem) => {
    setMenuOpen(false);
    if (item.route) {
      history.push(item.route);
    }
  };
  const openManagerChat = () => history.push("/pages/chat?channel=personal_manager");
  const openProfile = () => history.push(PROFILE_ROUTE);
  const onLogout = async () => {
    setUserMenuOpen(false);
    await dispatch(logoutUser());
    history.push("/login");
  };
  const userAvatar = (
    <span className="tw-relative tw-flex tw-size-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full">
      {resolvedUser.initials}
      <AvatarImage src={resolvedUser.avatarUrl} alt="" className="tw-rounded-full" />
    </span>
  );
  const renderAccountMenu = (idBase: string) =>
    userMenuOpen ? (
      <>
        <div className="tw-fixed tw-inset-0 tw-z-40" aria-hidden onClick={() => setUserMenuOpen(false)} />
        <div
          role="menu"
          className="tw-absolute tw-right-0 tw-top-full tw-z-50 tw-mt-1 tw-flex tw-min-w-48 tw-flex-col tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-popover tw-p-0.5 tw-shadow-md"
        >
          <button
            type="button"
            role="menuitem"
            data-testid={`${idBase}-profile`}
            onClick={() => {
              setUserMenuOpen(false);
              openProfile();
            }}
            className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-md tw-border-0 tw-bg-transparent tw-px-2 tw-py-1.5 tw-text-left tw-text-sm tw-text-foreground hover:tw-bg-accent"
          >
            <FormattedMessage id="redesign.profile.title" defaultMessage="My profile" />
          </button>
          <button
            type="button"
            role="menuitem"
            data-testid={`${idBase}-logout`}
            onClick={onLogout}
            className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-md tw-border-0 tw-bg-transparent tw-px-2 tw-py-1.5 tw-text-left tw-text-sm tw-text-foreground hover:tw-bg-accent"
          >
            <LogOutIcon className="tw-size-4 tw-text-muted-foreground" />
            <FormattedMessage id="redesign.shell.logout" defaultMessage="Log out" />
          </button>
        </div>
      </>
    ) : null;

  return (
    <RedesignToolbarContext.Provider value={toolbarContextValue}>
      <div
        className={cn(
          "tw-flex tw-w-full desktop:tw-bg-app",
          plainSurface ? "tw-bg-app" : "tw-bg-background",
          fullHeight ? "tw-h-dvh tw-overflow-hidden" : tightFooter ? "tw-min-h-dvh min-[1400px]:tw-h-dvh min-[1400px]:tw-max-h-dvh min-[1400px]:tw-overflow-hidden" : "tw-min-h-dvh",
        )}
      >
        <div data-testid="rd-shell-center" className="tw-mx-auto tw-flex tw-size-full tw-min-h-0 tw-max-w-[var(--rd-shell-block)]">
          <div
            className={cn(
              "tw-sticky tw-top-0 tw-z-20 tw-hidden tw-shrink-0 tw-py-6 tw-pl-6 desktop:tw-flex",
              tightFooter ? "tw-h-screen tw-self-start min-[1400px]:tw-h-full min-[1400px]:tw-self-auto" : "tw-h-screen tw-self-start",
            )}
          >
            {/* The nav owns vertical scrolling so the aside does not clip the enlarged manager avatar. */}
            <aside className="tw-flex tw-h-full tw-w-[295px] tw-flex-col tw-gap-1 tw-rounded-xl tw-bg-gradient-to-b tw-from-sidebar-from tw-to-sidebar-to">
              <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-px-5 tw-pb-6 tw-pt-8">{sidebarBrand}</div>

              {resolvedManager ? (
                <div className="tw-relative tw-z-20 tw-flex tw-w-full tw-justify-center tw-overflow-visible tw-px-[9.5px]">
                  <div className="tw-w-full tw-max-w-[276px]">
                    <ManagerCard manager={resolvedManager} tone="sidebar" onWrite={openManagerChat} />
                  </div>
                </div>
              ) : null}

              <nav data-testid="rd-sidebar-nav" className="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-gap-1 tw-overflow-y-auto tw-px-5 tw-py-2">
                <NavGroups activeNav={activeNav} navBadge={navBadge} tone="sidebar" onSelect={onSelect} />
              </nav>
            </aside>
          </div>

          <div
            className={cn(
              "tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col desktop:tw-pl-4 desktop:tw-pr-6",
              tightFooter ? "desktop:tw-py-4" : "desktop:tw-py-6",
              fullHeight && "tw-bg-background desktop:tw-bg-transparent",
            )}
          >
            <div className="tw-flex tw-flex-col">
              <header className="tw-flex tw-items-center tw-gap-3 tw-bg-sidebar-from tw-px-4 tw-py-3.5 desktop:tw-hidden">
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-justify-start">{mobileBarBrand}</div>
                <button
                  type="button"
                  data-testid="profile-menu-toggle-mobile"
                  aria-label={intl.formatMessage({ id: "top.menu.profile" })}
                  onClick={openProfile}
                  className="tw-flex tw-size-8 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-border-0 tw-bg-primary-foreground tw-p-0 tw-text-xs tw-font-semibold tw-text-foreground tw-ring-1 tw-ring-white/30"
                >
                  {userAvatar}
                </button>
              </header>

              <header className={cn("tw-hidden tw-w-full tw-items-center tw-gap-4 tw-px-6 desktop:tw-flex desktop:tw-max-w-rd-shell-content", tightFooter ? "tw-pb-2" : "tw-pb-4")}>
                {breadcrumbRoot || breadcrumbLeaf ? (
                  <nav aria-label={intl.formatMessage({ id: "redesign.shell.breadcrumbs", defaultMessage: "Breadcrumbs" })} className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-sm">
                    {breadcrumbRoot ? (
                      <>
                        <button type="button" onClick={() => (breadcrumbRoot.to ? history.push(breadcrumbRoot.to) : goCurrent())} className={crumbClass(!breadcrumbRoot.to && !breadcrumbLeaf)}>
                          <FormattedMessage id={breadcrumbRoot.id} defaultMessage={breadcrumbRoot.defaultMessage} />
                        </button>
                        {(breadcrumbTrail ?? []).map((crumb) => (
                          <React.Fragment key={`${crumb.id}-${crumb.to ?? ""}`}>
                            <ChevronRightIcon className="tw-size-4 tw-shrink-0 tw-text-muted-foreground" />
                            <button type="button" onClick={() => (crumb.to ? history.push(crumb.to) : goCurrent())} className={crumbClass(!crumb.to && !breadcrumbLeaf)}>
                              <FormattedMessage id={crumb.id} defaultMessage={crumb.defaultMessage} values={crumb.values} />
                            </button>
                          </React.Fragment>
                        ))}
                        {breadcrumbLeaf ? (
                          <>
                            <ChevronRightIcon className="tw-size-4 tw-shrink-0 tw-text-muted-foreground" />
                            <button type="button" aria-current="page" onClick={goCurrent} className={crumbClass(true)}>
                              <FormattedMessage id={breadcrumbLeaf.id} defaultMessage={breadcrumbLeaf.defaultMessage} values={breadcrumbLeaf.values} />
                            </button>
                          </>
                        ) : null}
                      </>
                    ) : breadcrumbLeaf && activeNavItem ? (
                      <>
                        <button type="button" onClick={() => (activeNavItem.route ? history.push(activeNavItem.route) : goCurrent())} className={crumbClass(false)}>
                          <FormattedMessage id={activeNavItem.id} defaultMessage={activeNavItem.defaultMessage} />
                        </button>
                        {(breadcrumbTrail ?? []).map((crumb) => (
                          <React.Fragment key={`${crumb.id}-${crumb.to ?? ""}`}>
                            <ChevronRightIcon className="tw-size-4 tw-shrink-0 tw-text-muted-foreground" />
                            <button type="button" onClick={() => (crumb.to ? history.push(crumb.to) : goCurrent())} className={crumbClass(false)}>
                              <FormattedMessage id={crumb.id} defaultMessage={crumb.defaultMessage} values={crumb.values} />
                            </button>
                          </React.Fragment>
                        ))}
                        <ChevronRightIcon className="tw-size-4 tw-shrink-0 tw-text-muted-foreground" />
                        <button type="button" aria-current="page" onClick={goCurrent} className={crumbClass(true)}>
                          <FormattedMessage id={breadcrumbLeaf.id} defaultMessage={breadcrumbLeaf.defaultMessage} values={breadcrumbLeaf.values} />
                        </button>
                      </>
                    ) : null}
                  </nav>
                ) : null}
                <div className="tw-relative tw-ml-auto tw-flex tw-items-center">
                  <span className="tw-mr-4 tw-text-sm tw-font-medium tw-text-foreground">{resolvedUser.name}</span>
                  <span className="tw-flex tw-size-10 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-primary-foreground tw-text-sm tw-font-semibold tw-text-foreground">
                    {userAvatar}
                  </span>
                  <button
                    type="button"
                    data-testid="profile-menu-toggle"
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    aria-label={intl.formatMessage({ id: "top.menu.profile" })}
                    onClick={() => setUserMenuOpen((v) => !v)}
                    className="tw-ml-2.5 tw-flex tw-size-4 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-muted-foreground hover:tw-text-foreground"
                  >
                    <ChevronDownIcon className={cn("tw-size-4 tw-transition-transform", userMenuOpen && "tw-rotate-180")} />
                  </button>
                  {renderAccountMenu("rd-account-menu")}
                </div>
              </header>
            </div>

            <div
              className={cn(
                "tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col desktop:tw-max-w-rd-shell-content",
                !plainSurface && "desktop:tw-rounded-xl desktop:tw-bg-background",
                !fullHeight && (tightFooter ? "tw-pt-3 desktop:tw-pt-4" : "tw-pt-3 desktop:tw-pt-6"),
              )}
            >
              <div ref={setToolbarSlot} className="tw-w-full tw-px-4 tw-pb-2 empty:tw-hidden" />

              <main className={cn("tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col", !fullHeight && "tw-px-4 tw-pb-24", !fullHeight && (tightFooter ? "desktop:tw-pb-0" : "desktop:tw-pb-4"))}>
                {children}
                {footer ?? (
                  <ShellFooter className={cn("tw-mt-auto tw-hidden tw-shrink-0 desktop:tw-flex", tightFooter ? "tw-pb-0 tw-pt-3 min-[1800px]:tw-pt-6" : "tw-pt-6", fullHeight && "tw-px-4 tw-pb-4")} />
                )}
              </main>
            </div>
          </div>
        </div>

        {menuOpen ? (
          <div
            data-testid="rd-mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label={intl.formatMessage({ id: "redesign.nav.more", defaultMessage: "More" })}
            className="tw-fixed tw-inset-0 tw-z-30 tw-flex tw-flex-col tw-overflow-y-auto tw-bg-gradient-to-b tw-from-sidebar-from tw-to-sidebar-to tw-pb-24 desktop:tw-hidden"
          >
            <header className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3.5">
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-justify-start">{mobileBarBrand}</div>
              <button
                type="button"
                aria-label={intl.formatMessage({ id: "top.menu.profile" })}
                onClick={() => {
                  setMenuOpen(false);
                  openProfile();
                }}
                className="tw-flex tw-size-8 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-border-0 tw-bg-background tw-p-0 tw-text-xs tw-font-semibold tw-text-foreground tw-ring-1 tw-ring-white/30"
              >
                {userAvatar}
              </button>
            </header>
            {resolvedManager ? (
              <div className="tw-px-3 tw-pb-2 tw-pt-3">
                <ManagerCard manager={resolvedManager} tone="sidebar" onWrite={openManagerChat} />
              </div>
            ) : null}
            <nav className="tw-flex tw-flex-col tw-gap-1 tw-px-4 tw-pt-5">
              <NavGroups activeNav={activeNav} navBadge={navBadge} tone="sidebar" onSelect={onSelect} />
              <Separator className="tw-mt-1.5 tw-bg-sidebar-separator" />
            </nav>
            <ShellFooter tone="dark" className="tw-px-4 tw-pb-2 tw-pt-4" />
          </div>
        ) : null}

        {/* Safe-area padding extends the 80px bar below its controls. */}
        <nav className="tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-40 tw-flex tw-h-[calc(5rem+env(safe-area-inset-bottom))] tw-items-stretch tw-border-t tw-border-solid tw-border-border tw-bg-background tw-px-2 tw-pb-[env(safe-area-inset-bottom)] desktop:tw-hidden">
          {BOTTOM_NAV.map((item) => {
            const { Icon } = item;
            const isMore = item.key === "more";
            const active = isMore ? menuOpen : !menuOpen && item.key === activeNav;
            const badge = isMore ? undefined : navBadge(item.key);
            return (
              <button
                key={item.key}
                type="button"
                aria-current={active ? "page" : undefined}
                aria-expanded={isMore ? menuOpen : undefined}
                onClick={() => (isMore ? setMenuOpen((v) => !v) : onSelect(item))}
                className={cn(
                  "tw-flex tw-flex-1 tw-cursor-pointer tw-appearance-none tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-border-0 tw-bg-transparent tw-py-2",
                  active ? "tw-text-foreground" : "tw-text-muted-foreground",
                )}
              >
                <span className="tw-relative tw-inline-flex">
                  <Icon className="tw-size-5" />
                  {badge ? (
                    <span className="tw-absolute -tw-right-[15px] -tw-top-2.5 tw-flex tw-size-4 tw-min-w-4 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-px-0.5 tw-font-sans tw-text-[0.5625rem] tw-font-medium tw-leading-none tw-text-primary-foreground">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  ) : null}
                </span>
                <span className={cn("tw-text-xs tw-leading-4", active && "tw-font-medium")}>
                  <FormattedMessage id={item.id} defaultMessage={item.defaultMessage} />
                </span>
              </button>
            );
          })}
        </nav>

        {!fullHeight && showScrollTop ? (
          <button
            type="button"
            aria-label={intl.formatMessage({ id: "redesign.shell.scrollToTop", defaultMessage: "Scroll to top" })}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="tw-fixed tw-bottom-24 tw-right-4 tw-z-30 tw-flex tw-size-12 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-[13px] tw-border-0 tw-bg-neutral-300 tw-text-foreground tw-shadow-md desktop:tw-hidden"
          >
            <ArrowUpIcon className="tw-size-[26px]" />
          </button>
        ) : null}
      </div>
    </RedesignToolbarContext.Provider>
  );
}
